import { Controller } from "@hotwired/stimulus";

const MOBILE_MAX_WIDTH = 1027;
const EMBED_HEIGHT_PADDING_PX = 24;
const EMBED_HEIGHT_DEBOUNCE_MS = 48;
const EMBED_HEIGHT_LOAD_POLL_MS = 100;
const EMBED_HEIGHT_AFTER_FRAME_LOAD_MS = 150;
const EMBED_HEIGHT_MAX_LOAD_POLLS = 100;

export default class extends Controller {
    connect() {
        this.boundHandleEvent = (event) => {
            if (event?.type === "turbo:load" || event?.type === "turbo-frame:load") {
                this._lastPostedEmbedHeight = null;
                this._embedHeightPollCount = 0;

                if (this.isInIframe() && this.isWorkflowTurboFrameEvent(event)) {
                    this.scheduleEmbedHeightPostMessage(EMBED_HEIGHT_AFTER_FRAME_LOAD_MS);
                    return;
                }

                if (
                    document.body.classList.contains("turbo-native") &&
                    (this.isWorkflowStepFrameEvent(event) ||
                        this.shouldResetTurboNativePageScroll(event))
                ) {
                    this.scheduleTurboNativeScrollSync({ resetScroll: true });
                }
            }

            if (
                event?.type === "turbo:morph" &&
                document.body.classList.contains("turbo-native") &&
                this.isTurboNativeWorkflowPage()
            ) {
                this.scheduleTurboNativeScrollSync({ resetScroll: true });
            }

            this.handleEvent();
        };

        this.navbarObserver = null;
        this.observedNavbar = null;
        this.embedHeightTimer = null;
        this.embedDocumentResizeObserver = null;
        this.stepLayoutObserver = null;
        this.turboNativeInsetObserver = null;
        this.turboNativeScrollTimer = null;
        this.turboNativeStepObserver = null;
        this._observedTurboNativeStepRoot = null;
        this._observedEmbedNodes = new Set();
        this._observedStepLayoutFrame = null;
        this._embedHeightPollCount = 0;
        this.activateEmbedAutoHeightMode();
        this.setupTurboNativeInsetObserver();
        this.handleEvent();
        if (document.body.classList.contains("turbo-native")) {
            this.scheduleTurboNativeScrollSync({ resetScroll: true });
        }
        window.addEventListener("resize", this.boundHandleEvent);
        window.addEventListener("scroll", this.boundScrollEvent);
        window.addEventListener("turbo-frame:load", this.boundHandleEvent);
        window.addEventListener("turbo:load", this.boundHandleEvent);
        window.addEventListener("turbo:morph", this.boundHandleEvent);
        if (window.visualViewport) {
            window.visualViewport.addEventListener("resize", this.boundHandleEvent);
        }
        this.boundOffcanvasHidden = () => {
            if (document.body.classList.contains("turbo-native")) {
                this.resetWorkflowScrollContainer();
            }
        };
        document.addEventListener("hidden.bs.offcanvas", this.boundOffcanvasHidden);
        this.setupEmbedDocumentResizeObserver();
        this.ensureEmbeddedSession();
        this.logEmbedDiagnosticsOnConnect();
    }

    disconnect() {
        window.removeEventListener("resize", this.boundHandleEvent);
        window.removeEventListener("scroll", this.boundScrollEvent);
        window.removeEventListener("turbo-frame:load", this.boundHandleEvent);
        window.removeEventListener("turbo:load", this.boundHandleEvent);
        window.removeEventListener("turbo:morph", this.boundHandleEvent);
        if (this.boundOffcanvasHidden) {
            document.removeEventListener("hidden.bs.offcanvas", this.boundOffcanvasHidden);
        }
        if (window.visualViewport) {
            window.visualViewport.removeEventListener("resize", this.boundHandleEvent);
        }
        this.teardownNavbarObserver();
        this.teardownTurboNativeInsetObserver();
        this.teardownTurboNativeStepObserver();
        this.teardownEmbedDocumentResizeObserver();
        this.teardownStepLayoutObserver();
        if (this.embedHeightTimer) {
            clearTimeout(this.embedHeightTimer);
            this.embedHeightTimer = null;
        }
        if (this.turboNativeScrollTimer) {
            clearTimeout(this.turboNativeScrollTimer);
            this.turboNativeScrollTimer = null;
        }
    }

    boundScrollEvent = () => {
        if (document.body.classList.contains("turbo-native")) return;
        this.boundHandleEvent({ type: "scroll" });
    };

    handleEvent() {
        this.setVh();
        this.setMobileNavbarOffset();
        this.remountNavbarObserverIfNeeded();
        if (!this.isInIframe()) {
            this.syncDocumentOverflow();
            this.setFillAvailableHeightElements();
            this.observeTurboNativeStepRoot();
        } else {
            this.syncEmbedDocumentOverflow();
            this.clearFillAvailableHeightElements();
        }
        this.observeEmbedContentNodes();
        this.scheduleEmbedHeightPostMessage();
    }

    isMobileLayout() {
        return window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`).matches;
    }

    setVh() {
        const vh = window.innerHeight * 0.01;
        document.documentElement.style.setProperty("--vh", `${vh}px`);
    }

    turboNativeTabBarInset() {
        const fromBody = getComputedStyle(document.body)
            .getPropertyValue("--turbo-native-tab-bar-inset")
            .trim();
        if (fromBody && fromBody !== "0px") return fromBody;

        return "calc(5rem + env(safe-area-inset-bottom, 0px))";
    }

    setupTurboNativeInsetObserver() {
        if (!document.body.classList.contains("turbo-native") || !window.MutationObserver) return;

        this.teardownTurboNativeInsetObserver();
        this.turboNativeInsetObserver = new MutationObserver(() => this.setMobileNavbarOffset());
        this.turboNativeInsetObserver.observe(document.body, {
            attributes: true,
            attributeFilter: ["style", "data-native-scroll-inset"],
        });
    }

    teardownTurboNativeInsetObserver() {
        if (this.turboNativeInsetObserver) {
            this.turboNativeInsetObserver.disconnect();
            this.turboNativeInsetObserver = null;
        }
    }

    setMobileNavbarOffset() {
        if (document.body.classList.contains("turbo-native")) {
            const inset = this.turboNativeTabBarInset();
            document.body.style.setProperty("--mobile-navbar-offset", inset);
            document.body.style.setProperty("--workflow-bottom-overlay-height", "0px");
            document.body.style.setProperty("--workflow-bottom-safe-area", inset);
            return;
        }

        if (!this.isMobileLayout()) {
            document.body.style.removeProperty("--mobile-navbar-offset");
            return;
        }

        if (document.body.dataset.mobileNavbar === "hidden") {
            document.body.style.removeProperty("--mobile-navbar-offset");
            return;
        }

        const nav = document.querySelector(".turbo-navbar");
        let px = 0;
        if (nav) {
            const style = window.getComputedStyle(nav);
            if (style.display !== "none" && style.visibility !== "hidden") {
                const h = nav.getBoundingClientRect().height;
                if (h > 0) px = h;
            }
        }
        document.body.style.setProperty("--mobile-navbar-offset", `${px}px`);
    }

    teardownNavbarObserver() {
        if (this.navbarObserver) {
            this.navbarObserver.disconnect();
            this.navbarObserver = null;
        }
        this.observedNavbar = null;
    }

    remountNavbarObserverIfNeeded() {
        const nav = document.querySelector(".turbo-navbar");
        if (!window.ResizeObserver || nav === this.observedNavbar) return;

        this.teardownNavbarObserver();
        if (!nav) return;

        this.observedNavbar = nav;
        this.navbarObserver = new ResizeObserver(() => this.setMobileNavbarOffset());
        this.navbarObserver.observe(nav);
    }

    syncDocumentOverflow() {
        if (document.body.classList.contains("turbo-native")) {
            if (this.isTurboNativeWorkflowPage()) {
                this.applyTurboNativeWorkflowScroll();
            } else {
                this.scheduleTurboNativeScrollSync();
            }
            return;
        }

        if (document.body.dataset.documentScroll === "true") {
            document.documentElement.style.overflow = "";
            document.body.style.overflow = "";
            return;
        }

        const mainCol = document.querySelector(".main-content-column");
        const scrollHost = mainCol?.querySelector(
            ".full-height-minus-header, .main-content, .full-height"
        );
        if (scrollHost) {
            document.documentElement.style.overflow = "hidden";
            document.body.style.overflow = "hidden";
            return;
        }

        if (!this.isMobileLayout()) {
            document.documentElement.style.overflow = "";
            document.body.style.overflow = "";
            return;
        }

        this.applyLegacyScrollableCheck();
    }

    syncEmbedDocumentOverflow() {
        document.documentElement.style.overflow = "";
        document.body.style.overflow = "";
    }

    applyLegacyScrollableCheck() {
        if (document.body.scrollHeight <= window.innerHeight) {
            document.documentElement.style.overflow = "hidden";
            document.body.style.overflow = "hidden";
        } else {
            document.documentElement.style.overflow = "";
            document.body.style.overflow = "";
        }
    }

    shouldResetTurboNativePageScroll(event) {
        if (event?.type !== "turbo:load") return false;

        const visit = event.detail?.visit;
        if (!visit) return true;

        if (visit.action === "restore") return false;

        return visit.action === "replace";
    }

    isWorkflowStepFrameEvent(event) {
        if (event?.type === "turbo:frame-load") {
            const frame = event.target;
            if (!frame?.id) return false;

            return (
                frame.id.startsWith("step_layout_") ||
                frame.id.includes("step_form_") ||
                frame.id.endsWith("_step_content")
            );
        }

        if (event?.type === "turbo:load") {
            return Boolean(document.querySelector('turbo-frame[id^="step_layout_"]'));
        }

        return false;
    }

    isTurboNativeWorkflowPage() {
        return Boolean(
            document.querySelector('turbo-frame[id^="step_layout_"]') ||
                document.querySelector('turbo-frame[id*="step_form_"]')
        );
    }

    turboNativeTabBarInsetPx() {
        const raw = this.turboNativeTabBarInset();
        const parsed = parseFloat(raw);
        return Number.isFinite(parsed) ? parsed : 80;
    }

    turboNativeWorkflowScrollContainer() {
        return (
            document.querySelector('turbo-frame[id^="step_layout_"] .step-layout') ||
            document.querySelector(".step-layout")
        );
    }

    resetWorkflowScrollContainer() {
        const container = this.turboNativeWorkflowScrollContainer();
        if (container) container.scrollTop = 0;
    }

    setTurboNativeWorkflowHeaderHeight() {
        if (!this.isTurboNativeWorkflowPage()) {
            document.documentElement.style.removeProperty("--workflow-web-header-height");
            return;
        }

        const header = document.querySelector(".header.border-bottom.sticky-top");
        const headerHeight = header ? Math.ceil(header.getBoundingClientRect().height) : 0;
        document.documentElement.style.setProperty(
            "--workflow-web-header-height",
            `${headerHeight}px`
        );
    }

    applyTurboNativeWorkflowScroll({ resetScroll = false } = {}) {
        document.documentElement.style.overflow = "hidden";
        document.body.style.overflow = "hidden";
        this.setTurboNativeWorkflowHeaderHeight();

        if (resetScroll) {
            this.resetWorkflowScrollContainer();
        }

        this.notifyNativeScroll({
            workflowInternalScroll: true,
            resetScroll: resetScroll,
            resyncInsets: resetScroll,
        });
    }

    turboNativeScrollMeasureRoot() {
        if (this.isTurboNativeWorkflowPage()) {
            return (
                this.turboNativeWorkflowScrollContainer() ||
                document.querySelector('turbo-frame[id*="step_form_"]')
            );
        }

        return (
            document.querySelector('turbo-frame[id*="step_form_"]') ||
            document.querySelector(".step-layout") ||
            document.querySelector(".main-content-column") ||
            document.body
        );
    }

    turboNativeContentNeedsScroll() {
        const viewportHeight = window.visualViewport?.height ?? window.innerHeight;

        // Measure with overflow unclamped — a prior short step may still have overflow:hidden.
        document.documentElement.style.overflow = "visible";
        document.body.style.overflow = "visible";

        const scrollHeight = Math.max(
            document.documentElement.scrollHeight,
            document.body.scrollHeight
        );

        return scrollHeight > viewportHeight + 2;
    }

    observeTurboNativeStepRoot() {
        if (!document.body.classList.contains("turbo-native") || !window.ResizeObserver) return;

        const root = this.turboNativeScrollMeasureRoot();
        if (!root || root === this._observedTurboNativeStepRoot) return;

        this.teardownTurboNativeStepObserver();
        this._observedTurboNativeStepRoot = root;
        this.turboNativeStepObserver = new ResizeObserver(() => {
            if (this.isTurboNativeWorkflowPage()) {
                this.setTurboNativeWorkflowHeaderHeight();
                this.applyTurboNativeWorkflowScroll();
            } else {
                this.notifyNativeScroll({ syncPageScroll: true });
                this.applyTurboNativeDocumentOverflow();
            }
        });
        this.turboNativeStepObserver.observe(root);
    }

    teardownTurboNativeStepObserver() {
        if (this.turboNativeStepObserver) {
            this.turboNativeStepObserver.disconnect();
            this.turboNativeStepObserver = null;
        }
        this._observedTurboNativeStepRoot = null;
    }

    resetTurboNativeScroll() {
        if (this.isTurboNativeWorkflowPage()) {
            this.applyTurboNativeWorkflowScroll({ resetScroll: true });
            return;
        }

        // WKWebView scroll must be reset from native code (adjustedContentInset.top), not scrollTo(0, 0).
        this.notifyNativeScroll({ resetScroll: true, resyncInsets: true });
    }

    notifyNativeScroll(payload) {
        const handler =
            window.webkit?.messageHandlers?.depozenModalScroll ||
            window.webkit?.messageHandlers?.depozenScroll;
        if (handler) {
            handler.postMessage(payload);
        }
    }

    scheduleTurboNativeScrollSync({ resetScroll = false } = {}) {
        if (!document.body.classList.contains("turbo-native")) return;

        if (this.isTurboNativeWorkflowPage()) {
            if (this.turboNativeScrollTimer) clearTimeout(this.turboNativeScrollTimer);
            this.turboNativeScrollTimer = setTimeout(() => {
                this.turboNativeScrollTimer = null;

                const run = (shouldReset) => {
                    requestAnimationFrame(() => {
                        this.applyTurboNativeWorkflowScroll({ resetScroll: shouldReset });
                        this.observeTurboNativeStepRoot();
                    });
                };

                run(resetScroll);

                if (resetScroll) {
                    [150, 400, 800].forEach((delay) => {
                        setTimeout(() => run(true), delay);
                    });
                }
            }, 0);
            return;
        }

        if (resetScroll) {
            this.notifyNativeScroll({ syncPageScroll: true });
        }

        if (this.turboNativeScrollTimer) clearTimeout(this.turboNativeScrollTimer);
        this.turboNativeScrollTimer = setTimeout(() => {
            this.turboNativeScrollTimer = null;

            const run = (shouldReset) => {
                requestAnimationFrame(() => {
                    if (shouldReset) this.resetTurboNativeScroll();
                    else this.applyTurboNativeDocumentOverflow();

                    this.observeTurboNativeStepRoot();
                    this.notifyNativeScroll({ workflowInternalScroll: false, syncPageScroll: true });
                });
            };

            run(resetScroll);

            if (resetScroll) {
                // Content height settles after Turbo morph — keep resetting scroll while it grows.
                [150, 400, 800, 1200, 1800].forEach((delay) => {
                    setTimeout(() => run(delay <= 800), delay);
                });
            }
        }, 0);
    }

    applyTurboNativeDocumentOverflow() {
        if (document.body.classList.contains("modal-open")) return;

        const needsScroll = this.turboNativeContentNeedsScroll();

        if (needsScroll) {
            document.documentElement.style.overflow = "";
            document.body.style.overflow = "";
        } else {
            document.documentElement.style.overflow = "hidden";
            document.body.style.overflow = "hidden";
            this.notifyNativeScroll({ resetScroll: true });
        }
    }

    setFillAvailableHeightElements() {
        if (document.body.classList.contains("turbo-native")) return;

        const elements = document.querySelectorAll(".fill-available-height");
        elements.forEach((element) => {
            if (element.closest(".workflow-step-shell__nav")) return;

            const elementTop = element.getBoundingClientRect().top;
            const availableHeight = window.innerHeight - elementTop;
            element.style.height = `${availableHeight}px`;
        });
    }

    clearFillAvailableHeightElements() {
        document.querySelectorAll(".fill-available-height").forEach((element) => {
            element.style.removeProperty("height");
        });
    }

    activateEmbedAutoHeightMode() {
        if (!this.isInIframe()) return;

        document.body.dataset.embedAutoHeight = "true";
        document.documentElement.dataset.embedAutoHeight = "true";
    }

    isInIframe() {
        return window.parent !== window;
    }

    // Iframe ouverte par un site tiers : une iframe de l'app elle-même (même origine) n'est pas un embed.
    isInThirdPartyIframe() {
        if (!this.isInIframe()) return false;

        try {
            return window.parent.location.origin !== window.location.origin;
        } catch {
            return true;
        }
    }

    ensureEmbeddedSession() {
        if (!this.isInThirdPartyIframe()) return;
        if (document.body?.dataset?.embedded === "true") {
            // Synchro confirmée : une session « désembarquée » plus tard (page ouverte en onglet) pourra se resynchroniser.
            sessionStorage.removeItem("depozen_embed_session_synced");
            return;
        }
        if (sessionStorage.getItem("depozen_embed_session_synced") === "1") return;

        const url = document.body?.dataset?.embedSessionUrl;
        if (!url) return;

        const csrfToken = document.querySelector('[name="csrf-token"]')?.content;
        if (!csrfToken) return;

        sessionStorage.setItem("depozen_embed_session_synced", "1");

        fetch(url, {
            method: "POST",
            headers: {
                "X-CSRF-Token": csrfToken,
                Accept: "text/vnd.turbo-stream.html, text/html, application/xhtml+xml",
            },
            credentials: "same-origin",
        })
            .then((response) => {
                if (!response.ok) throw new Error(`embed_session failed: ${response.status}`);

                if (window.Turbo) {
                    window.Turbo.visit(window.location.href, { action: "replace" });
                } else {
                    window.location.reload();
                }
            })
            .catch(() => {
                sessionStorage.removeItem("depozen_embed_session_synced");
            });
    }

    logEmbedDiagnosticsOnConnect() {
        if (!this.isInIframe()) return;

        console.log("[Depozen height] postMessage iframe actif");
    }

    isWorkflowTurboFrameEvent(event) {
        const frame = event?.target;
        if (!frame?.id) return false;

        return (
            frame.id.startsWith("step_layout_") ||
            frame.id.includes("step_form_") ||
            frame.id.endsWith("_step_content")
        );
    }

    embedStepLayoutFrame() {
        return document.querySelector('turbo-frame[id^="step_layout_"]');
    }

    isTurboFrameLoading(frame) {
        if (!frame) return false;
        if (frame.hasAttribute("busy")) return true;

        const src = frame.getAttribute("src");
        if (src && !frame.hasAttribute("complete")) return true;

        const loadingPlaceholder = frame.querySelector(
            ":scope > .fill-available-height .spinner-border, :scope > .fill-available-height [role='status']"
        );
        if (loadingPlaceholder) return true;

        return false;
    }

    isEmbedStepLoading() {
        const stepLayout = this.embedStepLayoutFrame();
        if (!stepLayout) return false;

        if (this.isTurboFrameLoading(stepLayout)) return true;

        const frames = stepLayout.querySelectorAll(
            'turbo-frame[id*="step_form_"], turbo-frame[id$="_step_content"]'
        );
        return Array.from(frames).some((frame) => this.isTurboFrameLoading(frame));
    }

    setupEmbedDocumentResizeObserver() {
        if (!this.isInIframe() || !window.ResizeObserver) return;

        this.embedDocumentResizeObserver = new ResizeObserver(() => {
            this.scheduleEmbedHeightPostMessage();
        });
        this.embedDocumentResizeObserver.observe(document.documentElement);
        if (document.body) this.embedDocumentResizeObserver.observe(document.body);
        this.observeEmbedContentNodes();
    }

    observeEmbedContentNodes() {
        if (!this.isInIframe() || !this.embedDocumentResizeObserver) return;

        const selectors = [
            'turbo-frame[id^="step_layout_"]',
            'turbo-frame[id$="_step_content"]',
            'turbo-frame[id*="step_form_"]',
            ".header.border-bottom.sticky-top",
            ".step-layout",
        ];

        selectors.forEach((selector) => {
            document.querySelectorAll(selector).forEach((node) => {
                if (this._observedEmbedNodes.has(node)) return;

                this._observedEmbedNodes.add(node);
                this.embedDocumentResizeObserver.observe(node);

                if (node.matches('turbo-frame[id^="step_layout_"]')) {
                    this.observeStepLayoutFrame(node);
                }
            });
        });
    }

    observeStepLayoutFrame(frame) {
        if (this._observedStepLayoutFrame === frame) return;

        this.teardownStepLayoutObserver();
        this._observedStepLayoutFrame = frame;

        this.stepLayoutObserver = new MutationObserver(() => {
            if (this.isEmbedStepLoading()) return;

            this._lastPostedEmbedHeight = null;
            this._embedHeightPollCount = 0;
            this.scheduleEmbedHeightPostMessage(EMBED_HEIGHT_AFTER_FRAME_LOAD_MS);
        });

        this.stepLayoutObserver.observe(frame, {
            attributes: true,
            attributeFilter: ["complete", "busy", "src"],
            childList: true,
            subtree: true,
        });
    }

    teardownStepLayoutObserver() {
        if (this.stepLayoutObserver) {
            this.stepLayoutObserver.disconnect();
            this.stepLayoutObserver = null;
        }
        this._observedStepLayoutFrame = null;
    }

    teardownEmbedDocumentResizeObserver() {
        if (this.embedDocumentResizeObserver) {
            this.embedDocumentResizeObserver.disconnect();
            this.embedDocumentResizeObserver = null;
        }
        this._observedEmbedNodes = new Set();
        this.teardownStepLayoutObserver();
    }

    scheduleEmbedHeightPostMessage(delay = EMBED_HEIGHT_DEBOUNCE_MS) {
        if (!this.isInIframe()) return;

        if (this.embedHeightTimer) clearTimeout(this.embedHeightTimer);
        this.embedHeightTimer = setTimeout(() => {
            this.embedHeightTimer = null;

            if (this.isEmbedStepLoading()) {
                if (this._embedHeightPollCount < EMBED_HEIGHT_MAX_LOAD_POLLS) {
                    this._embedHeightPollCount += 1;
                    this.scheduleEmbedHeightPostMessage(EMBED_HEIGHT_LOAD_POLL_MS);
                }
                return;
            }

            this._embedHeightPollCount = 0;
            requestAnimationFrame(() => {
                requestAnimationFrame(() => this.postEmbedHeightToParent());
            });
        }, delay);
    }

    embedMeasureRoot() {
        return (
            document.querySelector('turbo-frame[id^="step_layout_"]') ||
            document.querySelector('turbo-frame[id$="_step_content"]') ||
            document.querySelector('turbo-frame[id*="step_form_"]') ||
            document.querySelector(".main-content-column")
        );
    }

    workflowHeaderElement(stepLayout) {
        if (!stepLayout) return null;

        const previous = stepLayout.previousElementSibling;
        if (previous?.classList?.contains("header")) return previous;

        return stepLayout.parentElement?.querySelector(":scope > .header.border-bottom.sticky-top");
    }

    embedMeasureElements() {
        const stepLayout = document.querySelector('turbo-frame[id^="step_layout_"]');
        if (stepLayout) {
            const workflowHeader = this.workflowHeaderElement(stepLayout);
            return workflowHeader ? [workflowHeader, stepLayout] : [stepLayout];
        }

        const root = this.embedMeasureRoot();
        return root ? [root] : [];
    }

    measureEmbedHeight() {
        const elements = this.embedMeasureElements();

        if (elements.length > 0) {
            const bottom = Math.max(...elements.map((element) => this.elementBottomDocumentOffset(element)));
            return Math.ceil(bottom + EMBED_HEIGHT_PADDING_PX);
        }

        return Math.ceil(
            Math.max(
                document.documentElement.scrollHeight,
                document.body.scrollHeight,
                document.documentElement.offsetHeight,
                document.body.offsetHeight
            ) + EMBED_HEIGHT_PADDING_PX
        );
    }

    elementBottomDocumentOffset(element) {
        const rect = element.getBoundingClientRect();
        const top = rect.top + window.scrollY;
        const contentHeight = Math.max(element.scrollHeight, element.offsetHeight, rect.height);

        return top + contentHeight;
    }

    postEmbedHeightToParent() {
        if (!this.isInIframe()) return;
        if (this.isEmbedStepLoading()) return;

        const height = this.measureEmbedHeight();
        if (height === this._lastPostedEmbedHeight) return;
        this._lastPostedEmbedHeight = height;

        const payload = {
            type: "formulr:iframeModuleResized",
            size: `${height}px`,
        };

        console.log("[Depozen embed] postMessage → parent", payload);

        window.parent.postMessage(payload, "*");
    }
}
